import React, { useEffect, useState } from "react";
import "../styles/Candidates.scss";
import { Link, useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import {
  base_URL,
  deleteCandidate,
  getAllCandidates,
  getAllSkills,
  getCandidatesBySkills,
} from "../services/api";
import { AiOutlineEye } from "react-icons/ai";
import { FaEdit, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import ExcelJS from "exceljs"

const ITEMS_PER_PAGE = 10;

function Candidates() {
  const [candidates, setCandidates] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [searchSkills, setSearchSkills] = useState([]);
  const [skillsOptions, setSkillsOptions] = useState([]);
  const [skillInputValue, setSkillInputValue] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  // Fetch skills from API on component mount
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await getAllSkills();
        if (
          response &&
          response.result_code === 200 &&
          Array.isArray(response.result_info)
        ) {
          const skills = response.result_info.map(
            (skill) => skill.skill_name || skill.name || skill
          );
          setSkillsOptions(skills);
        }
      } catch (error) {
        console.error("Error fetching skills:", error);
      }
    };
    fetchSkills();
  }, []);

  // Reset to page 1 on filter change
  useEffect(() => {
    setPage(1);
  }, [searchName, searchSkills, candidates.length]);

  // Pagination handler (same as Requisitions)
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  // get all candidates information
  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        Swal.fire({
          html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
          showConfirmButton: false,
          heightAuto: false,
          customClass: { popup: "loader-swal" },
          allowOutsideClick: false,
        });
        const res = await getAllCandidates();
        Swal.close();
        if (res && res.status === "S" && Array.isArray(res.result_info)) {
          const mapped = res.result_info.map((c) => ({
            id: c.id,
            fullName: c.candidate_name,
            email: c.email,
            phone : c.phone,
            location: c.location,
            experience: c.years_of_exp,
            linkedin: c.linkedin_url,
            skills: c.skills
              ? Array.isArray(c.skills)
                ? c.skills
                : c.skills.split(/,\s*/)
              : [],
            present_ctc: c.present_ctc,
            expected_cdc: c.expected_cdc,
            resumeUrl: c.resume_path
              ? `${
                  process.env.REACT_APP_API_URL || base_URL
                }files/resume/${c.resume_path.replace(/\\/g, "/")}`
              : null,
            additional_notes: c.additional_notes,
            created_by: c.created_by,
            created_date: c.created_date,
            updated_at: c.updated_at,
            is_active: c.is_active,
          }));
          setCandidates(mapped);
        } else {
          setCandidates([]);
        }
      } catch (e) {
        setCandidates([]);
      }
    };
    fetchCandidates();
  }, []);

  // When searchSkills changes, prefer server-side search to ensure accurate skill matching
  useEffect(() => {
    const fetchBySkills = async () => {
      try {
        if (searchSkills && searchSkills.length > 0) {
          const skillsStr = searchSkills.join(',');
          const res = await getCandidatesBySkills(skillsStr);
          if (res && res.status === 'S' && Array.isArray(res.candidates)) {
            const mapped = res.candidates.map((c) => ({
              id: c.id,
              fullName: c.candidate_name,
              email: c.email,
              phone : c.phone,
              location: c.location,
              experience: c.years_of_exp,
              linkedin: c.linkedin_url,
              skills: c.skills
                ? Array.isArray(c.skills)
                  ? c.skills
                  : c.skills.split(/,\s*/)
                : [],
              present_ctc: c.present_ctc,
              expected_cdc: c.expected_cdc,
              resumeUrl: c.resume_path
                ? `${process.env.REACT_APP_API_URL || base_URL}files/resume/${c.resume_path.replace(/\\/g, '/')}`
                : null,
              additional_notes: c.additional_notes,
              created_by: c.created_by,
              created_date: c.created_date,
              updated_at: c.updated_at,
              is_active: c.is_active,
            }));
            setCandidates(mapped);
            return;
          }
        } else {
          // If no skills selected, fallback to fetching all candidates
          const res = await getAllCandidates();
          if (res && res.status === 'S' && Array.isArray(res.result_info)) {
            const mapped = res.result_info.map((c) => ({
              id: c.id,
              fullName: c.candidate_name,
              email: c.email,
              phone : c.phone,
              location: c.location,
              experience: c.years_of_exp,
              linkedin: c.linkedin_url,
              skills: c.skills
                ? Array.isArray(c.skills)
                  ? c.skills
                  : c.skills.split(/,\s*/)
                : [],
              present_ctc: c.present_ctc,
              expected_cdc: c.expected_cdc,
              resumeUrl: c.resume_path
                ? `${process.env.REACT_APP_API_URL || base_URL}files/resume/${c.resume_path.replace(/\\/g, '/')}`
                : null,
              additional_notes: c.additional_notes,
              created_by: c.created_by,
              created_date: c.created_date,
              updated_at: c.updated_at,
              is_active: c.is_active,
            }));
            setCandidates(mapped);
            return;
          }
        }
      } catch (err) {
        console.error('Error fetching candidates by skills:', err);
      }
    };

    fetchBySkills();
  }, [searchSkills]);

  // Filter candidates by name and skills
  const filteredCandidates = candidates.filter((c) => {
    const name = (c.fullName || c.name || "").toLowerCase();
    const nameMatch = name.includes(searchName.toLowerCase());
    const skillMatch =
      searchSkills.length > 0
        ? searchSkills.some((skill) =>
            (c.skills || [])
              .map((s) => (s ? s.toLowerCase() : ""))
              .includes(skill.toLowerCase())
          )
        : true;
    return nameMatch && skillMatch;
  });

  const pageCount = Math.ceil(filteredCandidates.length / ITEMS_PER_PAGE);
  const paginatedCandidates = filteredCandidates.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // Delete Candidate api integration
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      try {
        const res = await deleteCandidate(id);
        if (res && res.status === "S") {
          setCandidates((prev) => prev.filter((c) => c.id !== id));
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Candidate has been deleted.",
            timer: 1500,
            showConfirmButton: false,
          });
          // fetchCandidates();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text:
              res?.message || "Failed to delete candidate. Please try again.",
          });
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err?.message || "Failed to delete candidate. Please try again.",
        });
      }
    }
  };

  // Helper to format date as dd-mm-yyyy
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d)) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

   const handleExport = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Candidates");
    
        // Add header row
        worksheet.columns = [
          { header: "Name", key: "name" },
          { header: "Email", key: "email" },
          { header: "Phone", key: "phone" },
          { header: "Skills", key: "skills" },
          // { header: "Resume", key: "resume" },
          { header: "Updated Date", key: "updated_date" },
        ];
    
        // Add data rows
        candidates.forEach((item) => {
          worksheet.addRow({
            name: item.fullName || "-",
            email: item.email || "-",
            phone: item.phone || "-",
            skills: item.skills.join(", ") || "-",
            // resume: formatDate(item.created_at) || "-",
            updated_date: formatDate(item.updated_at) || "-",
          });
        });
    
        // Create a Blob from the workbook
        const buffer = await workbook.xlsx.writeBuffer();
    
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
    
        // Trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Candidates.xlsx";
        a.click();
        URL.revokeObjectURL(url);
      };

      function getPageNumbers(currentPage, totalPages, maxPagesToShow = 5) {
    const pages = [];

    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(currentPage - half, 1);
    let end = start + maxPagesToShow - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxPagesToShow + 1, 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }
  
  return (
    <div className="requisitions-page">
      <div className="requisitions-header-row">
        <div className="requisitions-title-group">
          <h1>Candidates</h1>
        </div>
        <div className="export-btn" onClick={handleExport}>
          Export
        </div>
        
      </div>
      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginBottom: "1.5rem",
          alignItems: "center",
          justifyContent: 'space-between'
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <input
          type="text"
          placeholder="Search by name"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          style={{
            minWidth: 180,
            maxWidth: 260,
            padding: "0.4rem .5rem",
            border: "1.5px solid #d1d5db",
            borderRadius: 6,
            background: "#fff",
            fontSize: "1rem",
          }}
          className="custom-placeholder"
          autoComplete="off"
                    
        />
        <CreatableSelect
          isMulti
          options={skillsOptions.map((s) => ({ value: s, label: s }))}
          value={searchSkills.map((s) => ({ value: s, label: s }))}
          onChange={(selected) => { if (!selected) { setSearchSkills([]);
                  return;}
          const uniqueSkills = [];
          selected.forEach((opt) => {
          const exists=uniqueSkills.some((skill) => skill.toLowerCase()===opt.value.toLowerCase());
          if (!exists) {
          uniqueSkills.push(opt.value);}});
          setSearchSkills(uniqueSkills);}}
          inputValue={skillInputValue}
          onInputChange={(val, { action }) => {
            if (action === 'input-change' || action === 'set-value') {
              setSkillInputValue(val);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              const normalized = (skillInputValue || '').trim();
              if (!normalized) return;
              const values = normalized
                .split(',')
                .map((val) => val.trim())
                .filter(Boolean);
              setSearchSkills((prev) => {
                const next = [...prev];
                values.forEach((value) => {
                  const alreadyExists = next.some(
                    (skill) => skill.toLowerCase() === value.toLowerCase()
                  );
                  if (!alreadyExists) next.push(value);
                });
                return next;
              });
              setSkillInputValue('');
            }
          }}
          onCreateOption={(inputValue) => {
            const normalized = inputValue.trim();
            if (!normalized) return;
            const values = normalized
              .split(',')
              .map((val) => val.trim())
              .filter(Boolean);
            setSearchSkills((prev) => {
              const next = [...prev];
              values.forEach((value) => {
                const alreadyExists = next.some(
                  (skill) => skill.toLowerCase() === value.toLowerCase()
                );
                if (!alreadyExists) next.push(value);
              });
              return next;
            });
            setSkillInputValue('');
          }}
          placeholder="Search by skills"
          isSearchable={true}
          classNamePrefix="react-select-location"
          formatCreateLabel={(inputValue) => `Search for "${inputValue}"`}
          noOptionsMessage={() =>
            skillInputValue
              ? `Press Enter to search for "${skillInputValue}"`
              : 'Type to search skills'
          }
          createOptionPosition="last"
    styles={{
    container: (base) => ({
      ...base,
      width: "340px",
      minWidth: "340px",
     maxWidth: "340px",
      }),

  control: (base) => ({
    ...base,
    minHeight: 38,
    maxHeight: 80,
    borderRadius: 6,
    fontSize: "1rem",
    background: "#fff",
    border: "1.5px solid #d1d5db",
    overflow: "hidden",
  }),

  valueContainer: (base) => ({
    ...base,
    maxHeight: 70,
    overflowY: "auto",
    overflowX: "hidden",
    flexWrap: "wrap",
    alignContent: "flex-start",
  }),

  multiValue: (base) => ({
    ...base,
    maxWidth: "120px",
    backgroundColor: "#e2e8f0",
  }),

  multiValueLabel: (base) => ({
    ...base,
    color: "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),

  indicatorsContainer: (base) => ({
    ...base,
    height: "100%",
  }),

  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#f3f4f6"
      : state.isFocused
      ? "#f8fafc"
      : "#fff",
    color: "#111827",
  }),
}}
        />
        </div>
        
        <div>
          <Link to="/candidates/create" className="create-btn">
          Create Candidate
        </Link>
        </div>
      </div>
      <div className="requisitions-table-wrapper">
        <table className="requisitions-table candidates-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Skills</th>
              <th>Resume</th>
              <th>Updated Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedCandidates.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    textAlign: "center",
                    padding: "2rem 0",
                  }}
                >
                  No candidates found
                </td>
              </tr>
            ) : (
              paginatedCandidates.map((candidate) => (
                <tr
                  key={candidate.id}
                  style={{ cursor: "pointer" }}
                  // onClick={(e) => {
                  //   if (
                  //     e.target.closest("a") ||
                  //     e.target.closest(".action-icons-row")
                  //   )
                  //     return;
                  //   navigate("/candidate-profile", {
                  //     state: {
                  //       candidate: {
                  //         ...candidate,
                  //         fullName: candidate.fullName || candidate.name || "",
                  //         dob: candidate.dob || "",
                  //         age: candidate.age || "",
                  //         gender: candidate.gender || "",
                  //         nationality: candidate.nationality || "",
                  //         linkedin: candidate.linkedin || "",
                  //         portfolio: candidate.portfolio || "",
                  //         github: candidate.github || "",
                  //       },
                  //     },
                  //   });
                  // }}
                >
                  <td>{candidate.fullName || candidate.name}</td>
                  <td>{candidate.email}</td>
                  <td>{candidate.phone || "-"}</td>
                  <td
                       title={
                         candidate.skills && candidate.skills.length > 0
                         ? candidate.skills.join(", ")
                         : "-"
                        }
                  >
                      <span className="candidate-skills-column">
                      {candidate.skills && candidate.skills.length > 0
                      ? candidate.skills.join(", ")
                      : "-"}
                     </span>
                  </td>
                  <td>
                    {candidate.resumeUrl ? (
                      <a
                        href={candidate.resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View Resume"
                        // onClick={(e) => e.stopPropagation()}
                      >
                        <AiOutlineEye
                          size={22}
                          color="#0078b9"
                          style={{
                            verticalAlign: "middle",
                          }}
                        />
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{formatDate(candidate.updated_at)}</td>
                  <td>
                    <div
                      className="action-icons-row"
                      style={{ display: "flex", gap: 10 ,alignItems: "center", justifyContent: "center"}}

                    >
                      <button
                        className="icon-btn edit-btn"
                        title="Edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/candidates/edit/${candidate.id}`);
                        }}
                      >
                        <FaEdit size={18} color="#0078B9" border="none" />
                      </button>
                      {/* <button
                        className="icon-btn delete-btn"
                        title="Delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(candidate.id);
                        }}
                      >
                        <FaTrash size={18} color="#e53e3e" />
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls (same as Requisitions) */}
      {pageCount > 1 && (
  <div className="settings-pagination">
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page - 1)}
      disabled={page === 1}
    >
      &lt;
    </button>
    {getPageNumbers(page, pageCount, 5).map((pageNum) => (
      <button
        key={pageNum}
        className={"pagination-btn" + (page === pageNum ? " active" : "")}
        onClick={() => handlePageChange(pageNum)}
      >
        {pageNum}
      </button>
    ))}
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page + 1)}
      disabled={page === pageCount}
    >
      &gt;
    </button>
  </div>
)}
    </div>
  );
}

export default Candidates;
